import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Box, Button, Divider, FormHelperText, Grid } from "@mui/material";

import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import RichTextEditor from "components/Shared/RichTextEditor";
import FileDropzone from "components/Shared/FileDropzone";

import { useDOUploadFile, useAuth } from "hooks";
import { formHookInputHelper } from "utils/formHookField";
import BaseSelect from "components/Shared/Base/BaseSelect";

const ResponseForm = ({
  isBusy = false,
  response,
  onSubmit = () => { },
  onCancel = () => { },
}) => {
  const typeEnums = ["Internal", "External"];
  const { viewEntity, isVendor } = useAuth();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      content: "",
      attachments: [],
      type: isVendor ? "External" : "External", // Default to External for both admin and vendor
    },
  });

  const { handleUploadFiles, uploadingFiles } = useDOUploadFile();

  const handleSubmitTicketResponse = async (data) => {
    const _responsePayload = { ...data };

    const [existingAttachments, newAttachments] = data.attachments.reduce(
      ([pass, fail], el) =>
        el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
      [[], []]
    );

    if (newAttachments.length) {
      const { uploadedFiles } = await handleUploadFiles(newAttachments, {
        folderName: "FEEDBACK_RESPONSE",
        entity: viewEntity?.entity?._id,
        account: viewEntity?.entity?.account?._id,
      });

      _responsePayload.attachments = [...existingAttachments, ...uploadedFiles];
    }

    onSubmit(_responsePayload);
  };

  React.useEffect(() => {
    if (!response) reset({ content: "", attachments: [], type: isVendor ? "External" : "External" });
    reset({ ...response, type: response?.type || (isVendor ? "External" : "External") });
  }, [reset, response, isVendor]);

  return (
    <LoadingWrapper loading={isBusy || uploadingFiles}>
      <InnerShadowBox padding={3}>
        <Grid container spacing={5}>
          <Grid item md={12}>
            <Controller
              name="type"
              control={control}
              rules={{ required: "Please select System Type." }}
              render={(props) => (
                <BaseSelect
                  {...formHookInputHelper(props)}
                  fullWidth
                  required
                  label="Type"
                  options={isVendor ? ["External"] : typeEnums} 
                  disabled={isVendor} 
                  error={!!errors?.type}
                  helperText={errors?.type?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={12}>
            <FormHelperText>Response</FormHelperText>

            <Controller
              name="content"
              control={control}
              rules={{ required: "Enter response." }}
              render={({ field: { onChange } }) => (
                <RichTextEditor
                  initialValue={
                    response?.content
                      ? response?.content
                      : "<p>Enter Response.</p>"
                  }
                  onChange={onChange}
                  height={200}
                />
              )}
            />
            {!!errors.content && (
              <FormHelperText error>{errors.content.message}</FormHelperText>
            )}
          </Grid>

          <Grid item md={12}>
            <FormHelperText>Attachments</FormHelperText>

            <Controller
              name="attachments"
              control={control}
              render={({ field }) => (
                <FileDropzone
                  multiple
                  maxFiles={5}
                  files={field?.value ?? []}
                  onFileChange={(files) => setValue("attachments", files)}
                />
              )}
            />
          </Grid>
        </Grid>
      </InnerShadowBox>

      <Divider />

      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        paddingX={3}
        paddingY={2}
      >
        <Button disabled={isBusy || uploadingFiles} onClick={onCancel}>
          Cancel
        </Button>

        <Button
          variant="outlined"
          disabled={isBusy || uploadingFiles}
          onClick={handleSubmit(handleSubmitTicketResponse)}
        >
          {response ? "Update" : "Add"}
        </Button>
      </Box>
    </LoadingWrapper>
  );
};

export default ResponseForm;